import { useState } from 'react';
import type { FormEvent } from 'react';
import { Gift, Mail, PenLine, Phone, Sparkles, Truck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Hero3D } from '@/components/three/Hero3D';
import { BrandArtwork } from '@/components/brand/BrandArtwork';
import { Parallax, RevealGroup, RevealItem, RevealLine } from '@/components/motion/Kinetic';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { usePlatform } from '@/providers/PlatformProvider';
import { usePageMetadata } from '@/hooks/usePageMetadata';
import { subscribeToLaunch } from '@/api/launch';
import { ApiException } from '@/types/api';

/**
 * Pre-launch holding page — BUILD-PLAN §24, §67.
 *
 * Shown INSTEAD of the shop before it opens. Distinct from the maintenance
 * screen, which apologises for being briefly down: this is the first thing
 * anyone sees of the brand, so it is a piece of marketing rather than a
 * notice, and it is built from the same design system as the rest of the site
 * — the rule-and-eyebrow lockup, restrained display type, the 3D box, the
 * inset keyline on every image.
 *
 * ## It has to earn something, not just look nice
 *
 * A holding page that only says "coming soon" spends the company's entire
 * pre-launch audience and keeps none of it. Everyone who lands here went
 * looking for this brand before it existed, which makes them the most
 * valuable list it will ever have. So the page explains what is actually
 * coming, and offers exactly one thing to do about it.
 *
 * The header and footer are shown but INERT — see `PublicLayout`. The company
 * is previewing the shape of the finished site, and stripping the navigation
 * would hide the very thing being previewed.
 */

/** What the shop will be, said concretely rather than in adjectives. */
const PROMISES: Array<{ icon: LucideIcon; title: string; body: string }> = [
  {
    icon: Gift,
    title: 'Curated gift boxes',
    body: 'Complete, considered boxes for birthdays, weddings, thank-yous and corporate gifting, chosen for how they feel to receive.',
  },
  {
    icon: PenLine,
    title: 'Personalised by hand',
    body: 'Names, initials, monograms and dates, engraved or printed by hand. You will see your personalisation applied before you order.',
  },
  {
    icon: Sparkles,
    title: 'Packaging as part of the gift',
    body: 'Ribbon, tissue, card and finish chosen together, so the unboxing is the first moment of the gift rather than an afterthought.',
  },
  {
    icon: Truck,
    title: 'Sent anywhere, tracked',
    body: 'Worldwide tracked delivery, gift notes, prices hidden from recipients, and named-day dispatch for the dates that matter.',
  },
];

export function ComingSoonPage() {
  const { config } = usePlatform();
  const { brand, contact } = config;

  usePageMetadata({
    title: 'Opening Soon',
    description: brand.description,
  });

  return (
    <div className="relative overflow-hidden">
      {/* ------------------------------------------------------------ hero */}
      <section className="mx-auto grid max-w-7xl grid-cols-12 items-center gap-y-14 px-5 py-14 sm:px-6 lg:px-8 lg:py-20">
        <div className="relative z-10 col-span-12 md:col-span-7 md:col-start-1 md:row-start-1 lg:col-span-6 lg:col-start-1 lg:pr-6">
          <div className="flex items-center gap-4">
            <span className="h-px w-10 bg-accent/60" aria-hidden="true" />
            <RevealLine className="eyebrow !mt-0">{brand.name}</RevealLine>
          </div>

          {/*
            Restrained, like every other heading on the site. A holding page
            is the easiest place to reach for enormous type, and it would then
            be the one screen that did not match the shop it introduces.
          */}
          <h1 className="mt-7 font-display text-[2rem] leading-[1.08] tracking-[-0.015em] text-ink min-[420px]:text-[2.5rem] sm:text-5xl">
            <RevealLine>Something beautiful</RevealLine>
            <RevealLine delay={0.12} className="block italic text-accent lg:pl-14">
              is being wrapped.
            </RevealLine>
          </h1>

          <RevealGroup className="mt-9" stagger={0.1}>
            <RevealItem>
              <p className="max-w-md border-l border-line pl-5 text-[0.9375rem] leading-[1.9] text-muted">
                {brand.description}
              </p>
            </RevealItem>

            <RevealItem className="mt-7">
              <p className="font-display text-xl italic text-accent sm:text-2xl">
                {brand.tagline}
              </p>
            </RevealItem>

            <RevealItem className="mt-9">
              <NotifyForm />
            </RevealItem>
          </RevealGroup>
        </div>

        {/* --------------------------------------------------------- artwork */}
        <div className="relative col-span-12 md:col-span-5 md:row-start-1 lg:col-span-7 lg:col-start-6 lg:row-start-1">
          {/*
            The drifting panel is positioned against the FRAME, not the
            column, which is wider than the image. Measured from the column it
            floated in the gutter, attached to nothing.
          */}
          <div className="relative mx-auto w-full max-w-xs sm:max-w-sm lg:ml-auto lg:mr-0 lg:max-w-md">
            <div className="relative aspect-4/5 w-full shadow-raised">
              <Hero3D seed={brand.tagline} className="h-full w-full" />

              {/* The same inset keyline every image on the site carries. */}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-4 border border-ivory/15"
              />
            </div>

            {/*
              Shown at EVERY size, phones included.
              ------------------------------------------------------------
              This was `hidden lg:block`, then `hidden sm:block` — and `sm`
              starts at 640px, so on an actual phone it stayed hidden and the
              composition lost half its layering on the device most visitors
              arrive on. "Not desktop" is not a breakpoint; if a detail is
              part of the design it needs a size that fits a 390px screen,
              not a rule that removes it.

              The offsets stay inside the frame's own margin at 390px, so
              nothing pushes the page sideways.
            */}
            <Parallax
              distance={40}
              className="absolute -bottom-6 -left-4 aspect-square w-20 shadow-card sm:-bottom-8 sm:-left-6 sm:w-28 lg:-bottom-10 lg:-left-12 lg:w-36 xl:w-44"
            >
              <BrandArtwork seed={`${brand.name}-ribbon`} motif="ribbon" ratio="square" />
            </Parallax>
          </div>

          {/* Centred on small screens, where the frame is centred too. */}
          <p className="mt-8 flex items-center justify-center gap-3 text-[0.6875rem] uppercase tracking-[0.25em] text-muted lg:justify-end">
            <span className="h-px w-8 bg-line" aria-hidden="true" />
            Opening soon
          </p>
        </div>
      </section>

      {/* --------------------------------------------------- what is coming */}
      <section className="border-t border-line bg-surface-muted/40">
        <div className="mx-auto max-w-7xl px-5 py-14 sm:px-6 lg:px-8 lg:py-20">
          <div className="flex items-center gap-3">
            <span className="h-px w-8 bg-accent/50" aria-hidden="true" />
            <p className="eyebrow !mt-0">What we are building</p>
          </div>

          <h2 className="mt-4 max-w-2xl font-display text-[1.75rem] leading-[1.15] text-ink sm:text-3xl lg:text-[2.25rem]">
            A gift shop for the moments that deserve more than an afterthought
          </h2>

          {/*
            One column on a phone: two would crush four blocks of real copy
            into unreadable slivers. The grid opens up as soon as there is
            room for it.
          */}
          <ul className="mt-10 grid gap-x-10 gap-y-9 sm:grid-cols-2 lg:mt-12 lg:grid-cols-4 lg:gap-x-8">
            {PROMISES.map(({ icon: Icon, title, body }) => (
              <li key={title} className="border-t border-line pt-6">
                <Icon className="h-5 w-5 text-accent" strokeWidth={1.5} aria-hidden="true" />

                <h3 className="mt-4 font-display text-lg text-ink">{title}</h3>

                <p className="mt-2.5 text-sm leading-[1.85] text-muted">{body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ---------------------------------------------------------- contact */}
      {(contact['email'] || contact['phone']) && (
        <section className="border-t border-line">
          <div className="mx-auto flex max-w-7xl flex-col gap-6 px-5 py-12 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
            <div>
              <div className="flex items-center gap-3">
                <span className="h-px w-8 bg-accent/50" aria-hidden="true" />
                <p className="eyebrow !mt-0">Enquiries</p>
              </div>

              <p className="mt-3 max-w-md text-sm leading-[1.8] text-muted">
                Planning corporate gifting, a wedding or an event? Talk to us now, we are
                taking bespoke enquiries ahead of opening.
              </p>
            </div>

            {/*
              Only the channels that are actually configured — §86, nothing
              here is a control that cannot do anything.
            */}
            <ul className="flex flex-col gap-3 text-sm text-muted sm:flex-row sm:gap-6">
              {contact['email'] && (
                <li>
                  <a
                    href={`mailto:${contact['email']}`}
                    className="inline-flex items-center gap-2.5 transition-colors hover:text-accent"
                  >
                    <Mail className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                    {contact['email']}
                  </a>
                </li>
              )}

              {contact['phone'] && (
                <li>
                  <a
                    href={`tel:${contact['phone']}`}
                    className="inline-flex items-center gap-2.5 transition-colors hover:text-accent"
                  >
                    <Phone className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                    {contact['phone']}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </section>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */

/**
 * The one thing this page can actually do.
 *
 * A real endpoint, not a decorative field. A form that appears to capture an
 * address and quietly drops it is worse than no form at all, because the
 * visitor believes they will be told and never is (§86).
 */
function NotifyForm() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);

  /*
   * A plain async submit rather than a mutation.
   *
   * There is one request, no cache to invalidate and no shared server state
   * to keep in step, so a mutation adds a layer without adding anything —
   * and a layer that swallows its own failures is worse than none on a form
   * whose entire job is to succeed visibly or say why it did not.
   */
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const address = email.trim();

    if (address === '') return;

    setState('sending');
    setError(null);

    try {
      await subscribeToLaunch(address);
      setEmail('');
      setState('done');
    } catch (caught) {
      setState('idle');

      /*
       | The server's own message when it gave one — a validation complaint
       | about the address is far more useful than "something went wrong" —
       | and a plain fallback for anything else (§41).
       */
      setError(
        caught instanceof ApiException
          ? (Object.values(caught.fieldErrors())[0] ?? caught.message)
          : 'We could not save that just now. Please try again in a moment.',
      );
    }
  }

  if (state === 'done') {
    return <Alert tone="success">Thank you, we will let you know the moment we open.</Alert>;
  }

  return (
    <form noValidate onSubmit={submit} className="max-w-md">
      <div className="flex items-center gap-3">
        <span className="h-px w-8 bg-accent/50" aria-hidden="true" />
        <p className="eyebrow !mt-0">Be first to know</p>
      </div>

      <p className="mt-3 text-sm leading-[1.8] text-muted">
        Leave your email and we will tell you the day the collection opens, nothing else.
      </p>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="flex-1">
          <Input
            label="Email address"
            type="email"
            value={email}
            required
            autoComplete="email"
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        {/* Offset so the button aligns with the input, not with its label. */}
        <div className="sm:mt-7">
          <Button type="submit" loading={state === 'sending'}>
            Notify me
          </Button>
        </div>
      </div>

      {error && (
        <div className="mt-4">
          <Alert tone="error">{error}</Alert>
        </div>
      )}
    </form>
  );
}
